// 1. React
import React, { useState, useEffect, useRef, useMemo } from 'react';

// 2. Third-party
import { Form, Steps, Button, Upload, message, AutoComplete } from 'antd';
import { UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import SignatureCanvas from 'react-signature-canvas';
import FormBuilder from 'antd-form-builder';
import queryString from 'query-string';

// 3. Components / constants / services
import { normalizeFieldWidget, isSignatureField, isFieldVisible, withRowDividers } from '@iso/constants/libraryFieldWidgets';
import { visitorFormService, attachment } from '@iso/services';
// Side-effect: registers the shared string widgets (text / date-view / fixed_text /
// fixed_image / scale / signature+upload placeholders) exactly once. This renderer
// overrides signature/upload_file per field below with the REAL components.
import '@iso/lib/helpers/schemaFormWidgets';

const { Step } = Steps;

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Join API_BASE + a (possibly slash-less) server path with exactly one separator, so
// the stored url never becomes "http://host uploads/..." (missing slash) — see the
// signature/upload answer refs consumed by the View Result viewer.
const joinApiUrl = (path) =>
  `${API_BASE.replace(/\/+$/, '')}/${String(path || '').replace(/^\/+/, '')}`;

const isPublicCheckinPath = (pathname) =>
  pathname === '/visitor-form' ||
  pathname === '/contractor-form' ||
  pathname === '/public-form' ||
  pathname === '/property-checkin';

// Anonymous uploads are capped server-side (params file_limit_size.public).
// Checked here too so the visitor is told why, not just that it failed — the
// upload services drop the server's message on a non-success code. The thrown
// `userMessage` is shown as-is by handleSubmit.
export const MAX_PUBLIC_UPLOAD_MB = 5;
export const assertPublicUploadSize = (file) => {
  if (file && file.size > MAX_PUBLIC_UPLOAD_MB * 1024 * 1024) {
    const err = new Error('File too large');
    err.userMessage = `You cannot upload file over ${MAX_PUBLIC_UPLOAD_MB}MB`;
    throw err;
  }
};

// Public check-in upload via the token-gated endpoint (a real visitor on a live
// primary form). Returns a { name, path, url, type } ref or null.
const uploadViaToken = async (file, formToken) => {
  assertPublicUploadSize(file);
  const fd = new FormData();
  fd.append('File', file);
  fd.append('visitor_form_token', formToken);
  fd.append('type', 'checkin_upload');
  const res = await visitorFormService.uploadFile(fd);
  if (res && res.file && res.file.path) {
    // Prefer the FULL storage URL the endpoint returns (S3/Azure object URL, or the
    // backend host for filesystem). Only fall back to API_BASE + path for older
    // backends that don't return `url` — which is wrong for S3-hosted files.
    const url = res.file.url || joinApiUrl(res.file.path);
    return { name: res.file.name || file.name, path: res.file.path, url, storage: res.file.storage, type: res.file.type || file.type };
  }
  return null;
};

// Authed upload via the attachment endpoint — the same one the legacy upload-file
// question uses. Returns a { name, path, url, ... } ref or null.
const uploadViaAttachment = async (file) => {
  let serverType = 'file';
  if (file.type && file.type.startsWith('image/')) serverType = 'image';
  else if (file.type && file.type.startsWith('video/')) serverType = 'video';
  const resp = await attachment.uploadFile(file, serverType);
  const att = resp && resp.attachment;
  const url = (att && (att.full_path || att.url)) || '';
  return url
    ? { name: file.name, path: (att && att.url) || '', url, attachment_id: (att && att.id) || null, type: file.type }
    : null;
};

// Upload one file, returning a stored { name, path, url, type } ref (or null). A
// logged-in user (in-app OR an admin previewing) uploads via the authed attachment
// endpoint first — like the legacy upload-file question; a truly anonymous public
// visitor uses the token-gated endpoint first. Each order falls back to the other.
const uploadFileToServer = async (file, ctx) => {
  const { isPublic, formToken, uploadFile } = ctx || {};
  // An explicit uploader wins. Some public forms are not gated by a form token
  // at all — a visitor induction link carries a per-induction token, and the
  // token-gated endpoint below only resolves PRIMARY CHECK-IN forms — so the
  // caller supplies the upload that matches its own gate.
  if (typeof uploadFile === 'function') {
    return uploadFile(file);
  }
  const authed = !!localStorage.getItem('id_token');
  let attempts;
  if (authed) {
    attempts = (isPublic && formToken)
      ? [() => uploadViaAttachment(file), () => uploadViaToken(file, formToken)]
      : [() => uploadViaAttachment(file)];
  } else if (isPublic && formToken) {
    attempts = [() => uploadViaToken(file, formToken), () => uploadViaAttachment(file)];
  } else {
    attempts = [() => uploadViaAttachment(file)];
  }
  for (let i = 0; i < attempts.length; i += 1) {
    try {
      const ref = await attempts[i]();
      if (ref) {
        return ref;
      }
    } catch (e) {
      // A reason the visitor can act on (e.g. the size limit) ends the attempts:
      // falling back to /attachments would only 401 and hide it.
      if (e && e.userMessage) {
        throw e;
      }
      console.warn('Upload attempt failed, trying next', e);
    }
  }
  return null;
};

// Runtime file upload. The chosen file is held LOCALLY and only uploaded on submit
// (see handleSubmit) — so if the user re-picks a different file, earlier picks are
// never uploaded to the server; only the final file is. A held file is
// { file, name, type, pending: true }; a revisited/already-stored answer is a
// { name, url, ... } ref.
const RuntimeUpload = ({ value, onChange }) => {
  const beforeUpload = (file) => {
    onChange && onChange({ file, name: file.name, type: file.type, pending: true });
    return false; // never auto-upload; the form uploads the final file on submit
  };

  const fileList = value && value.name
    ? [{ uid: '-1', name: value.name, status: 'done', url: value.url }]
    : [];

  return (
    <Upload
      beforeUpload={beforeUpload}
      maxCount={1}
      fileList={fileList}
      onRemove={() => onChange && onChange(null)}
    >
      <Button icon={<UploadOutlined />}>Upload file</Button>
    </Upload>
  );
};

// Simple inline signature pad: the user draws directly on the canvas. The drawing is
// held (and submitted) as a base64 data URL — mirroring the legacy survey form's
// signature: the base64 is posted in the payload (no file upload) and the answer
// viewer renders it inline. `value` may be a bare base64 string (submitted / revisited)
// or the transient `{ dataUrl }` while drawing (see handleSubmit).
const InlineSignaturePad = ({ value, onChange }) => {
  const sigRef = useRef(null);

  // Restore a previously-drawn (but not-yet-uploaded) signature when the field
  // remounts — e.g. navigating back to its step in a multi-step form.
  useEffect(() => {
    if (value && value.dataUrl && !value.url && sigRef.current && sigRef.current.isEmpty()) {
      sigRef.current.fromDataURL(value.dataUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnd = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      onChange && onChange(null);
      return;
    }
    onChange && onChange({ dataUrl: sigRef.current.getTrimmedCanvas().toDataURL('image/png') });
  };

  const clear = () => {
    if (sigRef.current) { sigRef.current.clear(); }
    onChange && onChange(null);
  };

  // A TYPED signature, from a channel that has no canvas — a chat thread has no
  // way to draw, so it takes a full name instead and records it against the
  // verified sending number. It is still a string, so it used to land in the
  // <img> below and render as a broken image: the answer was there and looked
  // missing.
  const isImageSrc = (v) => typeof v === 'string'
    && (v.startsWith('data:image') || v.startsWith('http') || v.startsWith('/'));

  if (typeof value === 'string' && value && !isImageSrc(value)) {
    return (
      <div>
        <span style={{ fontFamily: 'cursive', fontSize: 20 }}>{value}</span>
        <div style={{ color: '#888', fontSize: 12 }}>Typed signature</div>
      </div>
    );
  }

  // Already-captured (revisit / after submit): show the stored image. The value is
  // a bare base64 string now, or a legacy `{ url }` ref from an older submission.
  const capturedSrc = typeof value === 'string' ? value : (value && value.url);
  if (capturedSrc) {
    return (
      <div>
        <img src={capturedSrc} alt="signature" style={{ maxWidth: 420, maxHeight: 150, border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff' }} />
        <div><Button type="link" size="small" icon={<DeleteOutlined />} onClick={clear}>Clear</Button></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, background: '#fff', display: 'inline-block', lineHeight: 0 }}>
        <SignatureCanvas
          ref={sigRef}
          penColor="black"
          onEnd={handleEnd}
          canvasProps={{ width: 420, height: 150, style: { touchAction: 'none' } }}
        />
      </div>
      <div>
        <span style={{ color: '#999', fontSize: 12, marginRight: 8 }}>Sign above</span>
        <Button type="link" size="small" icon={<DeleteOutlined />} onClick={clear}>Clear</Button>
      </div>
    </div>
  );
};

// "Property Area / Location": suggestions from the property's Areas, and
// anything else the person types. Deliberately NOT a Select — contractors are
// regularly sent somewhere that was never drawn on the map (a neighbour's shed,
// a roadside gate), and a list they cannot escape would make the field
// unanswerable. The value stored is always the text.
const AreaAutoComplete = ({ areaOptions, ...props }) => (
  <AutoComplete
    {...props}
    style={{ width: '100%', ...(props && props.style) }}
    options={areaOptions || []}
    // Substring, case-insensitive: "shed" should find "Back Shed", which a
    // prefix match would miss.
    filterOption={(input, option) =>
      String((option && option.value) || '').toLowerCase().includes(String(input || '').toLowerCase())
    }
    placeholder="Type, or choose one of the property's areas"
    allowClear
  />
);

// Map a schema field to its RUNTIME field meta: host-picker -> select of hosts,
// area-picker -> autocomplete of the property's areas, signature/upload -> real
// components; everything else normalized as usual.
const runtimeField = (field, hosts, areas) => {
  let f = field;
  if (f.widget === 'host-picker') {
    // A curated List Hosts field (configured in the property builder) carries
    // hostOptions = [{ userId, label }] — use it verbatim. Otherwise fall back to
    // ALL of the property's hosts (legacy / library-defined host fields).
    const { hostOptions, ...rest } = f;
    const options = Array.isArray(hostOptions) && hostOptions.length
      ? hostOptions.map((o) => ({ value: o.userId, label: o.label }))
      : (hosts || []).map((h) => ({ value: h.id, label: h.host_display || h.user_name || `Host ${h.id}` }));
    f = { ...rest, widget: 'select', options };
  }
  f = normalizeFieldWidget(f);
  // After normalisation, like signature/upload below: the swap puts a COMPONENT
  // in `widget`, and normalizeFieldWidget only understands widget names — it
  // would have nothing to say about a function and could only get in the way.
  if (f.widget === 'area-picker') {
    // A field can carry its own curated list (areaOptions, like hostOptions);
    // otherwise it offers the property's areas as they stand today. Either way
    // the field still accepts free text, so an empty list costs nothing — it
    // degrades to a plain input.
    const { areaOptions, ...rest } = f;
    const options = Array.isArray(areaOptions) && areaOptions.length
      ? areaOptions.map((o) => ({ value: typeof o === 'string' ? o : o.label || o.name }))
      : (areas || [])
          .map((a) => ({ value: typeof a === 'string' ? a : a.name }))
          .filter((o) => o.value);
    f = {
      ...rest,
      widget: AreaAutoComplete,
      widgetProps: { ...(rest.widgetProps || {}), areaOptions: options },
    };
  }
  if (f.widget === 'signature') {
    f = { ...f, widget: InlineSignaturePad };
  }
  if (f.widget === 'upload_file') {
    f = { ...f, widget: RuntimeUpload };
  }
  return f;
};

const isDisplayOnly = (widget) =>
  widget === 'fixed_text'
  || widget === 'fixed_image'
  || widget === 'fixed_pdf'
  || widget === 'divider';

// antd-form-builder registers each field's Form name as key.split('.'), so a dotted
// key (e.g. "name.first") is a NESTED path. Read such a value from the (nested)
// answers object, and detect an empty answer (for the required-completeness guard).
const getAnswerByPath = (values, key) => String(key).split('.').reduce((o, k) => (o == null ? undefined : o[k]), values);
const isAnswerBlank = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

// A field carrying `requiredVisitorTypes` (e.g. List Hosts) is required only when the
// form's Visitor Types answer (key `visitor_types`) is one of those values.
const requiredByVisitorType = (field, values) => {
  const req = field && field.requiredVisitorTypes;
  if (!Array.isArray(req) || !req.length) {
    return false;
  }
  const answer = values ? values.visitor_types : undefined;
  const reqStr = req.map(String);
  if (Array.isArray(answer)) {
    return answer.some((v) => reqStr.indexOf(String(v)) !== -1);
  }
  return reqStr.indexOf(String(answer)) !== -1;
};

// Whether a field is required given the current answers (static `required`, or a
// visitor-type-conditional requirement).
const isFieldRequired = (field, values) => !!(field && (field.required || requiredByVisitorType(field, values)));

/**
 * Interactive multi-step renderer for a form's schema_json (Phase 5 runtime).
 * Props: { form (has schema_json), hosts, areas, loading, onSubmit(answers), formToken?, uploadFile? }
 *
 * `areas` is [{ id, name }] (or bare strings) — the property's Areas, offered as
 * suggestions by any area-picker field. Optional everywhere: without it those
 * fields are plain text inputs, which is the honest fallback.
 * formToken: explicit public form_token (required on /property-checkin where the
 * URL token is the property landing token, not the form token).
 * uploadFile: optional async (file) => ref override for upload_file questions,
 * for callers whose files are gated by something other than a form token (the
 * public visitor-induction link). Omitted everywhere else, which keeps the
 * existing check-in upload behaviour untouched. It may throw an Error carrying
 * `userMessage`, which is shown instead of the generic upload failure.
 */
const SchemaFormRenderer = ({ form, hosts, areas, loading, onSubmit, formToken: formTokenProp, uploadFile }) => {
  const [rendererForm] = Form.useForm();
  const [current, setCurrent] = useState(0);
  // Top of the form body, for the step-change scroll below.
  const topRef = useRef(null);
  const hasRenderedRef = useRef(false);
  const [hiddenIds, setHiddenIds] = useState([]);
  // Snapshot of all answers (across steps), refreshed on every change so per-field
  // `visibleWhen` conditions can be re-evaluated as the user fills the form.
  const [formValues, setFormValues] = useState({});
  // True while the final upload_file selections are being uploaded on submit.
  const [submitting, setSubmitting] = useState(false);

  const publicCtx = useMemo(() => {
    const queryToken = queryString.parse(window.location.search).token || '';
    const path = window.location.pathname;
    // On property landing, never use the landing token for uploads — use form token.
    if (path === '/property-checkin') {
      const formToken = formTokenProp || (form && form.form_token) || '';
      return { isPublic: !!formToken, formToken, uploadFile };
    }
    const formToken = formTokenProp || queryToken;
    return {
      isPublic: isPublicCheckinPath(path) && !!formToken,
      formToken,
      uploadFile,
    };
  }, [formTokenProp, form, uploadFile]);

  const steps = form && form.schema_json && Array.isArray(form.schema_json.steps) ? form.schema_json.steps : [];

  // Keys of signature fields — their drawn { dataUrl } values become base64 on submit.
  const signatureKeys = [];
  // Keys of upload_file fields — their held files are uploaded on submit (only the
  // final selection per field, never intermediate re-picks).
  const uploadKeys = [];
  steps.forEach((step) => {
    ((step.formMeta && step.formMeta.fields) || []).forEach((field) => {
      if (field.key && (field.widget === 'signature' || isSignatureField(field))) {
        signatureKeys.push(field.key);
      }
      if (field.key && field.widget === 'upload_file') {
        uploadKeys.push(field.key);
      }
    });
  });

  useEffect(() => {
    setCurrent(0);
    setHiddenIds([]);
    setFormValues({});
  }, [form]);

  // switch -> hiddenId step-hide rules
  const hideRules = [];
  steps.forEach((step) => {
    ((step.formMeta && step.formMeta.fields) || []).forEach((field) => {
      if (field.widget === 'switch' && field.hiddenId) {
        hideRules.push({ key: field.key, hiddenId: field.hiddenId });
      }
    });
  });

  // On every change, refresh the step-hide rules AND the cross-step answer snapshot
  // that per-field `visibleWhen` conditions are evaluated against.
  const onValuesChange = () => {
    const ids = [];
    hideRules.forEach((rule) => {
      if (rendererForm.getFieldValue(rule.key)) {
        ids.push(rule.hiddenId);
      }
    });
    setHiddenIds(ids);
    setFormValues(rendererForm.getFieldsValue(true));
  };

  const visibleSteps = steps.filter((step) => !(step.id && hiddenIds.indexOf(step.id) !== -1));
  const activeIndex = visibleSteps.length ? Math.min(current, visibleSteps.length - 1) : 0;
  const activeStep = visibleSteps[activeIndex];
  // Only fields whose `visibleWhen` condition is satisfied are rendered/validated.
  const visibleStepFields = (step) => (((step && step.formMeta && step.formMeta.fields) || [])
    .filter((f) => isFieldVisible(f, formValues)));
  const activeMeta = activeStep && activeStep.formMeta
    ? {
        ...activeStep.formMeta,
        formItemLayout: null,
        gutter: 32,
        // Dividers go in LAST, over the visible fields only — a rule under a
        // field that is conditionally hidden would otherwise be left floating
        // with nothing above it.
        fields: withRowDividers(
          visibleStepFields(activeStep).map((f) => {
            // Apply a visitor-type-conditional requirement (e.g. List Hosts required
            // when the visitor type is one of its requiredVisitorTypes) as a live rule.
            const rf = runtimeField(f, hosts, areas);
            return isDisplayOnly(f.widget) ? rf : { ...rf, required: isFieldRequired(f, formValues) };
          }),
          activeStep.formMeta.columns
        ),
      }
    : null;

  // Name PATHS for validateFields — must match how antd-form-builder registers each
  // field (key.split('.')), so dotted keys like "name.first" are validated too.
  const stepFieldNames = (step) => visibleStepFields(step)
    .filter((f) => f.key && !isDisplayOnly(f.widget))
    .map((f) => f.key.split('.'));

  // Put the top of the form back in view whenever the step changes.
  //
  // A long step leaves the reader at the bottom of the page; without this,
  // Next swaps the questions out underneath them and the new step starts
  // somewhere in its middle, which reads as nothing having happened. It also
  // covers the jump-back-to-the-first-unanswered-step on submit, where landing
  // mid-step would hide the very field being complained about.
  //
  // scrollIntoView on an anchor INSIDE the form, rather than scrolling the
  // window: the renderer is used both on a page (public induction, form
  // submission) and inside a modal (check-in, Run Induction), and this way the
  // browser scrolls whichever ancestor actually scrolls.
  useEffect(() => {
    // Not on first render — a form appearing should not move the page.
    if (!hasRenderedRef.current) {
      hasRenderedRef.current = true;
      return;
    }
    const el = topRef.current;
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }, [current]);

  const goNext = () => {
    rendererForm.validateFields(stepFieldNames(activeStep)).then(() => setCurrent(activeIndex + 1)).catch(() => {});
  };

  const goPrev = () => setCurrent(activeIndex - 1);

  const handleSubmit = () => {
    rendererForm.validateFields().then(async () => {
      // Collect the answers from EVERY step, not just the one on screen. A multi-step
      // form renders only the active step, so validateFields() resolves with just that
      // step's mounted fields — and a display-only last step (e.g. fixed_image) would
      // resolve to {}. antd keeps unmounted field values (preserve defaults to true),
      // so getFieldsValue(true) returns the full cross-step answer set.
      const values = rendererForm.getFieldsValue(true);
      // Drop answers for fields hidden by an unmet `visibleWhen` condition — they
      // are not shown, so they must not be recorded or validated (mirrors the
      // server-side skip in SchemaFormService::validateSubmission).
      steps.forEach((step) => {
        ((step.formMeta && step.formMeta.fields) || []).forEach((f) => {
          if (f.key && !isFieldVisible(f, values)) {
            delete values[f.key];
          }
        });
      });
      // Guard: every VISIBLE required field across ALL steps must be answered. antd's
      // validateFields() only checks the mounted step, so a multi-step form could
      // otherwise submit with earlier-step required fields empty. Verify against the
      // collected values and jump back to the first offending (non-hidden) step.
      for (let si = 0; si < visibleSteps.length; si += 1) {
        const stepFields = (visibleSteps[si].formMeta && visibleSteps[si].formMeta.fields) || [];
        const missing = stepFields.find((f) => f.key
          && isFieldRequired(f, values)
          && !isDisplayOnly(f.widget)
          && isFieldVisible(f, values)
          && isAnswerBlank(getAnswerByPath(values, f.key)));
        if (missing) {
          setCurrent(si);
          // Surface the field markers on that step once it re-mounts.
          setTimeout(() => rendererForm.validateFields(stepFieldNames(visibleSteps[si])).catch(() => {}), 0);
          message.error('Please complete all required fields before submitting.');
          return;
        }
      }
      // Signatures: post the base64 data URL directly in the payload (no file upload),
      // mirroring the legacy survey form. The transient drawing value is `{ dataUrl }`;
      // replace it with the raw base64 string so it is stored/rendered as-is.
      signatureKeys.forEach((key) => {
        const v = values[key];
        if (v && typeof v === 'object' && v.dataUrl) {
          values[key] = v.dataUrl;
        }
      });
      // Upload the final held file for each upload_file field NOW (not on selection),
      // so re-picked/earlier files are never uploaded. Replace the held file with its
      // stored { name, path, url } ref. An already-uploaded ref (no `pending`) is kept.
      const pendingUploads = uploadKeys.filter((key) => {
        const v = values[key];
        return v && typeof v === 'object' && v.pending && v.file;
      });
      if (pendingUploads.length) {
        setSubmitting(true);
        try {
          for (let i = 0; i < pendingUploads.length; i += 1) {
            const key = pendingUploads[i];
            const ref = await uploadFileToServer(values[key].file, publicCtx);
            if (!ref) {
              message.error('Could not upload the file. Please try again.');
              setSubmitting(false);
              return;
            }
            values[key] = ref;
          }
        } catch (e) {
          console.error(e);
          // An uploadFile override may throw with a reason the visitor can act
          // on (e.g. the public size limit).
          message.error((e && e.userMessage) || 'Could not upload the file. Please try again.');
          setSubmitting(false);
          return;
        }
        setSubmitting(false);
      }
      onSubmit && onSubmit(values);
    }).catch(() => {});
  };

  if (!visibleSteps.length || !activeMeta) {
    return null;
  }

  const isLast = activeIndex === visibleSteps.length - 1;

  return (
    <Form form={rendererForm} layout="vertical" onValuesChange={onValuesChange} className="schema-form-area">
        {/* Scroll anchor. An empty div rather than a ref on the Form itself:
            antd v4 gives Form's `ref` the FormInstance, not the element. */}
        <div ref={topRef} aria-hidden="true" style={{ scrollMarginTop: 12 }} />
        {visibleSteps.length > 1 && (
          <Steps current={activeIndex} size="small" style={{ marginBottom: 16 }}>
            {visibleSteps.map((step, index) => (
              <Step key={step.id || index} title={step.title || `Step ${index + 1}`} />
            ))}
          </Steps>
        )}

        <FormBuilder meta={activeMeta} form={rendererForm} />

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeIndex === 0} onClick={goPrev}>
            Previous
          </Button>
          {isLast ? (
            <Button type="primary" className="btn-success" loading={loading || submitting} onClick={handleSubmit}>
              Submit
            </Button>
          ) : (
            <Button type="primary" className="btn-success" onClick={goNext}>
              Next
            </Button>
          )}
        </div>
      </Form>
  );
};

export default SchemaFormRenderer;
